import React, { Component } from 'react';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import LoadingSpinner from '../common/LoadingSpinner';
import { renderIcon } from '../utils/Common';
import Events from './Events';
import Emails from './Emails';

class RelatedRecords extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      records: [],
      headers: {},
      currentPage: 0,
      totalRecords: 0,
      limit: 10,
      hasMore: false,
      loadingMore: false,
    };
    this.scrollContainerRef = React.createRef();
  }

  componentDidMount() {
    this.loadRelatedRecords();
  }

  componentWillUnmount() {
    // Clean up scroll listener
    if (this.scrollContainerRef.current) {
      this.scrollContainerRef.current.removeEventListener('scroll', this.handleScroll);
    }
  }
  loadRelatedRecords = async (page = 0) => {
    const { recordId, relationId, module, onLogout } = this.props;
    
    // Determine the operation based on module type
    let operation = 'GetRelatedRecords';
    if (module === 'Emails') {
      operation = 'GetRelatedEmails';
    } else if (module === 'Calendar') {
      operation = 'GetRelatedEvents';
    }

    // Set loading state based on whether this is initial load or load more
    if (page === 0) {
      this.setState({ loading: true });
    } else {
      this.setState({ loadingMore: true });
    }
    
    try {
      let result;
      
      if (module === 'Emails' || module === 'Calendar') {
        // Use special API call for Emails and Calendar
        result = await apiService.getSpecialRelatedRecords(
          operation,
          recordId,
          relationId,
          module,
          page,
          this.state.limit
        );
      } else {
        // Use regular API call for other modules
        result = await apiService.getRelatedRecords(
          recordId,
          relationId,
          module,
          page,
          this.state.limit
        );
      }
      
      
      if (result.success) {
        const responseData = result.data;
        
        // Handle the new API response structure
        const records = responseData.listViewEntries || responseData.data || responseData.records || [];
        const totalCount = responseData.total_count || responseData.totalRecords || records.length;
        const headers = responseData.listViewHeaders || {};
        
        
        const newRecords = records;
        
        // Calculate if there are more records to load
        const currentRecordCount = page === 0 ? newRecords.length : this.state.records.length + newRecords.length;
        const hasMore = currentRecordCount < totalCount;
        
        this.setState({
          records: page === 0 ? newRecords : [...this.state.records, ...newRecords],
          headers: headers,
          currentPage: page,
          totalRecords: totalCount,
          hasMore: hasMore,
          loading: false,
          loadingMore: false,
        });
        
        // Add scroll listener after first load
        if (page === 0 && this.scrollContainerRef.current) {
          this.scrollContainerRef.current.addEventListener('scroll', this.handleScroll);
        }
      } else if (result.needsLogout && onLogout) {
        onLogout();
      } else {
        this.setState({ 
          loading: false, 
          loadingMore: false,
          records: page === 0 ? [] : this.state.records 
        });
      }
    } catch (error) {
      this.setState({ 
        loading: false, 
        loadingMore: false,
        records: page === 0 ? [] : this.state.records 
      });
    }
  };

  handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const { hasMore, loadingMore } = this.state;
    
    // Load more when scrolled to bottom (with 100px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loadingMore) {
      this.loadMoreRecords();
    }
  };

  loadMoreRecords = () => {
    if (this.state.hasMore && !this.state.loadingMore) {
      const nextPage = this.state.currentPage + 1;
      this.loadRelatedRecords(nextPage);
    }
  };

  refreshRecords = () => {
    this.setState({
      records: [],
      currentPage: 0,
      hasMore: false,
    }, () => {
      this.loadRelatedRecords(0);
    });
  };

  // Filter out fields we don't want to display
  shouldDisplayField = (fieldKey, fieldValue) => {
    // Skip empty, zero, or unwanted fields
    if (!fieldValue || fieldValue === '0' || fieldValue === 0) return false;
    
    // Skip ID fields and internal fields
    const skipFields = ['id', 'invoiceid', 'accountid', 'smownerid', 'subject'];
    if (skipFields.includes(fieldKey)) return false;
    
    return true;
  };

  handleRecordClick = (record) => {
    // Handle record click - could open detail view or edit form
    if (this.props.onRecordSelect) {
      this.props.onRecordSelect(record);
    }
  };

  formatFieldValue = (fieldKey, fieldValue) => {
    // Format currency fields
    if (fieldKey === 'total' || fieldKey === 'hdnGrandTotal') {
      return `$${parseFloat(fieldValue).toFixed(2)}`;
    }
    
    // Format date fields
    if (fieldKey === 'createdtime' || fieldKey.includes('date')) {
      try {
        return new Date(fieldValue).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return fieldValue;
      }
    }
    
    // Format status fields with badges
    if (fieldKey.includes('status') || fieldKey === 'cf_962') {
      const statusColors = {
        'Pending': 'bg-yellow-100 text-yellow-800',
        'Approved': 'bg-green-100 text-green-800',
        'Rejected': 'bg-red-100 text-red-800',
        'Draft': 'bg-gray-100 text-gray-800',
        'Active': 'bg-blue-100 text-blue-800',
        'Inactive': 'bg-gray-100 text-gray-800'
      };
      
      const colorClass = statusColors[fieldValue] || 'bg-gray-100 text-gray-800';
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
          {fieldValue}
        </span>
      );
    }
    
    // Default formatting
    return fieldValue;
  };
  render() {
    const { 
      loading, 
      records, 
      headers,
      totalRecords, 
      hasMore,
      loadingMore 
    } = this.state;
    
    const { relationLabel, onCancel, onBack, module, recordId } = this.props;

    if (loading) {
      return (
        <LoadingSpinner label="Loading related records..." variant="fullscreen" />
      );
    }

    // Show Emails component for Emails module
    if (module === 'Emails') {
      return (
        <Emails
          recordId={recordId}
          relationId={this.props.relationId}
          onBack={onBack}
          onCancel={onCancel}
          onLogout={this.props.onLogout}
        />
      );
    }

    // Show Events component for Calendar module
    if (module === 'Calendar') {
      return (
        <Events
          recordId={recordId}
          relationId={this.props.relationId}
          onBack={onBack}
          onCancel={onCancel}
          onLogout={this.props.onLogout}
        />
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 space-y-3">
          {/* Back Button */}
          <div>
            {onBack ? (
              <button
                onClick={onBack}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back to Details
              </button>
            ) : (
              <button
                onClick={onCancel}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back
              </button>
            )}
          </div>
          
          {/* Title and Refresh */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {relationLabel || 'Related Records'}
            </h2>
            <button
              onClick={this.refreshRecords}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Pagination Summary */}
          <div>
            <p className="text-sm text-gray-500">
              Showing {records.length} of {totalRecords} records
            </p>
          </div>
        </div>

        {/* Records List */}
        <div 
          ref={this.scrollContainerRef}
          className="overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 200px)' }}
          onScroll={this.handleScroll}
        >
          {records.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {records.map((record, index) => (
                <div
                  key={index}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => this.handleRecordClick(record)}
                >
                  {/* Record Title */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-medium text-gray-900 truncate">
                      {record.label || record.name || 'Unnamed Record'}
                    </h3>
                    <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                  
                  {/* Record Fields - Generic Display */}
                  <div className="space-y-2">
                    {Object.entries(headers).map(([fieldKey, fieldLabel]) => {
                      const fieldValue = record[fieldKey];
                      if (this.shouldDisplayField(fieldKey, fieldValue)) {
                        return (
                          <div key={fieldKey} className="flex items-start justify-between py-1">
                            <span className="text-sm font-medium text-gray-600 flex-shrink-0 w-1/3">
                              {fieldLabel}:
                            </span>
                            <span className="text-sm text-gray-900 text-right flex-1 ml-2">
                              {this.formatFieldValue(fieldKey, fieldValue)}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })}
                    
                    {/* Created Time - Always show if available */}
                    {record.createdtime && (
                      <div className="flex items-start justify-between py-1 border-t border-gray-100 pt-2 mt-2">
                        <span className="text-sm font-medium text-gray-600 flex-shrink-0 w-1/3">
                          Created:
                        </span>
                        <span className="text-sm text-gray-500 text-right flex-1 ml-2">
                          {new Date(record.createdtime).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Load More Indicator */}
              {loadingMore && (
                <div className="flex justify-center py-4 px-4">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    <span className="text-sm text-gray-500">Loading more records...</span>
                  </div>
                </div>
              )}
              
              {/* End of Records Indicator */}
              {!hasMore && records.length > 0 && (
                <div className="text-center py-4 px-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400">No more records to load</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 px-4">
              <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                {renderIcon('Detail')}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Records Found</h3>
              <p className="text-sm text-gray-500">No related records found for this item</p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default RelatedRecords;